import { ArrowLeft, ArrowRight, Mail, Smartphone } from 'lucide-react';
import { Button } from '@/shared/components/Button';
import { Checkbox } from '@/shared/components/Checkbox';
import { FormField } from '@/shared/components/FormField';
import { Icon } from '@/shared/components/Icon';
import { Input } from '@/shared/components/Input';
import { cn } from '@/shared/helpers/cn';
import type { LoginOutcome, TwoFactorRequiredOutcome } from '../models/auth.model';
import { useTwoFactorViewModel } from '../viewmodels/useTwoFactorViewModel';
import styles from './TwoFactorPanel.module.scss';

export interface TwoFactorPanelProps {
  challenge: TwoFactorRequiredOutcome;
  onAuthenticated: (outcome: LoginOutcome) => void;
  onCancel: () => void;
}

/**
 * Verificação do dispositivo.
 *
 * Aparece quando o navegador é novo ou está sem verificação recente. Dois passos:
 * escolher por onde receber o código e digitá-lo. Os contatos vêm mascarados do
 * servidor.
 */
export function TwoFactorPanel({ challenge, onAuthenticated, onCancel }: TwoFactorPanelProps) {
  const vm = useTwoFactorViewModel(challenge, onAuthenticated);

  if (vm.step === 'choose-channel') {
    return (
      <div className={styles.panel}>
        <div className={styles.intro}>
          <h2 className={styles.title}>Confirme sua identidade</h2>
          <p className={styles.subtitle}>
            Não reconhecemos este dispositivo. Escolha como receber o código de verificação.
          </p>
        </div>

        <div className={styles.channels}>
          {vm.channels.email ? (
            <button
              type="button"
              className={cn(styles.channel, vm.selectedChannel === 'email' && styles.channelActive)}
              onClick={() => vm.selectChannel('email')}
              aria-pressed={vm.selectedChannel === 'email'}
            >
              <span className={styles.channelIcon}>
                <Icon as={Mail} size={20} />
              </span>
              <span className={styles.channelLabel}>E-mail</span>
              <span className={styles.channelValue}>{vm.channels.email}</span>
            </button>
          ) : null}

          {vm.channels.phone ? (
            <button
              type="button"
              className={cn(styles.channel, vm.selectedChannel === 'phone' && styles.channelActive)}
              onClick={() => vm.selectChannel('phone')}
              aria-pressed={vm.selectedChannel === 'phone'}
            >
              <span className={styles.channelIcon}>
                <Icon as={Smartphone} size={20} />
              </span>
              <span className={styles.channelLabel}>Telefone</span>
              <span className={styles.channelValue}>{vm.channels.phone}</span>
            </button>
          ) : null}
        </div>

        {vm.error ? (
          <p className={styles.error} role="alert">
            {vm.error}
          </p>
        ) : null}

        <div className={styles.actions}>
          <Button
            size="lg"
            block
            disabled={!vm.selectedChannel}
            loading={vm.isSending}
            onClick={() => void vm.sendCode()}
            icon={<Icon as={ArrowRight} size={20} />}
          >
            Receber código
          </Button>
          <Button variant="tertiary" onClick={onCancel} icon={<Icon as={ArrowLeft} size={16} />}>
            Voltar ao login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <form
      className={styles.panel}
      onSubmit={(event) => {
        event.preventDefault();
        vm.submit();
      }}
    >
      <div className={styles.intro}>
        <h2 className={styles.title}>Digite o código</h2>
        <p className={styles.subtitle}>
          Enviamos um código de 4 dígitos para <strong>{vm.sentTo}</strong>. Ele vale por 5 minutos.
        </p>
      </div>

      <FormField label="Código de verificação" error={vm.form.formState.errors.code?.message}>
        {(props) => (
          <Input
            {...props}
            {...vm.form.register('code')}
            className={styles.code}
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={4}
            placeholder="0000"
            autoFocus
          />
        )}
      </FormField>

      <Checkbox
        label="Lembrar deste dispositivo por 14 dias"
        hint="Nos próximos acessos deste navegador, o código não será pedido."
        checked={vm.rememberDevice}
        onChange={(event) => vm.setRememberDevice(event.target.checked)}
      />

      {vm.error ? (
        <p className={styles.error} role="alert">
          {vm.error}
        </p>
      ) : null}

      <div className={styles.actions}>
        <Button type="submit" size="lg" block loading={vm.isVerifying}>
          Confirmar
        </Button>
        <div className={styles.secondary}>
          <Button variant="tertiary" loading={vm.isSending} onClick={() => void vm.resendCode()}>
            Reenviar código
          </Button>
          <Button variant="tertiary" onClick={vm.backToChannels}>
            Trocar canal
          </Button>
        </div>
      </div>
    </form>
  );
}
